import { useCallback, useEffect, useState } from 'react';

const KEY_PREFIX = 'atek-profile-photo:';
const EVENT_NAME = 'atek-profile-photo-change';

function storageKey(userId: string | undefined | null): string | null {
  return userId ? `${KEY_PREFIX}${userId}` : null;
}

function read(userId: string | undefined | null): string | null {
  const key = storageKey(userId);
  if (!key) return null;
  try { return localStorage.getItem(key); } catch { return null; }
}

/**
 * Profile photo (data URL) cached per-user in localStorage.
 * Reads and writes fire a custom event so every mounted component updates
 * immediately when the photo changes.
 */
export function useProfilePhoto(userId: string | undefined | null): [string | null, (next: string | null) => void] {
  const [photo, setPhoto] = useState<string | null>(() => read(userId));

  useEffect(() => {
    setPhoto(read(userId));
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent<{ userId: string }>).detail;
      if (!detail || detail.userId === userId) setPhoto(read(userId));
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey(userId)) setPhoto(e.newValue);
    };
    window.addEventListener(EVENT_NAME, onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, [userId]);

  const update = useCallback((next: string | null) => {
    const key = storageKey(userId);
    if (!key) return;
    try {
      if (next) localStorage.setItem(key, next);
      else localStorage.removeItem(key);
    } catch { /* quota */ }
    setPhoto(next);
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { userId } }));
  }, [userId]);

  return [photo, update];
}
